import React, { useContext } from 'react'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import { SERVICE_FEE_PERCENTAGE } from 'constants/payment'
import { getDepositAmountI18NKeyDetails } from 'lib/TourV2/depositAmountStringBuilder'
import VerticalSpacer from '../Spacing/VerticalSpacer'
import { LE_CONTRACTED_HOTEL_TYPES, OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_TOUR } from 'constants/offer'
import { getMinDepositAmount, getAlwaysOnCancellationDays, isOfferDefaultDepositAmountPercentage } from 'lib/depositsUtils'
import { isTourV2OfferType } from 'lib/offerTypes'
import GeoContext from 'contexts/geoContext'
import { INELEGIBLE_SERVICE_FEE_OFFER_TYPES } from 'constants/payment'

import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import { useAppSelector } from 'hooks/reduxHooks'
import { useTranslation } from 'react-i18next'

// HOW IS THIS DIFFERENT TO getPaymentDueDate IN LIB/DEPOSITUTILS?
const getPaymentDueDaysBeforeCheckin = (offerType: App.OfferType, checkIn, offerPackage, pkgAvailableRate, depositThresholds) => {
  if (depositThresholds && depositThresholds.numberOfDays) {
    return depositThresholds.numberOfDays
  }

  if (offerType == OFFER_TYPE_ALWAYS_ON) {
    const freeCancellationDays = getAlwaysOnCancellationDays(checkIn, offerPackage, pkgAvailableRate?.cancellationPolicies)
    return (freeCancellationDays) ? freeCancellationDays + 15 : 15
  }

  return (offerType == OFFER_TYPE_TOUR || isTourV2OfferType(offerType)) ? 105 : 60
}

const getOfferEligibilityDaysInAdvance = (offerType: App.OfferType, checkIn, offerPackage, pkgAvailableRate, depositThresholds) => {
  if (depositThresholds && depositThresholds.numberOfDays) {
    return depositThresholds.numberOfDays + 1
  }

  if (offerType == OFFER_TYPE_ALWAYS_ON) {
    // @ts-ignore: this defaults to 16 when function returns null
    return getAlwaysOnCancellationDays(checkIn, offerPackage, pkgAvailableRate?.cancellationPolicies) + 16
  }

  return ((offerType === OFFER_TYPE_TOUR || isTourV2OfferType(offerType))) ? 106 : 61
}

function getOfferTypeString(offerType: App.OfferType) {
  switch (offerType) {
    case OFFER_TYPE_TOUR:
    case 'direct_tour':
    case 'partner_tour':
    case 'connection_tour':
      return 'Tour'
    case OFFER_TYPE_ALWAYS_ON:
      return 'Lux Premium Collection'
    default:
      return 'Limited Time LUX Exclusive'
  }
}

export interface Props {
  offerType: App.OfferType;
  offerPackage?: App.Package;
  pkgAvailableRate?: App.OfferAvailableRate;
  checkIn?: moment.Moment;
  depositAmountPercentage?: number
  depositThresholds?: App.DepositThreshold
  depositType?: Tours.V2OfferDepositType
  isFlexibleCancellation?: boolean;
  depositAmount?: number;
}

const DepositInfoModalContent = ({
  offerType,
  offerPackage,
  pkgAvailableRate,
  checkIn,
  depositAmountPercentage,
  depositThresholds,
  depositType,
  isFlexibleCancellation,
  depositAmount,
}: Props) => {
  const { currentCurrency } = useContext(GeoContext)
  const offerEligibilityDaysInAdvance = getOfferEligibilityDaysInAdvance(offerType, checkIn, offerPackage, pkgAvailableRate, depositThresholds)
  const paymentDueDaysBeforeCheckin = getPaymentDueDaysBeforeCheckin(offerType, checkIn, offerPackage, pkgAvailableRate, depositThresholds)
  const isHotel = LE_CONTRACTED_HOTEL_TYPES.includes(offerType)
  const minAmount = getMinDepositAmount(currentCurrency)

  const { t } = useTranslation(['common', 'deposit'])

  const offerTypeString = getOfferTypeString(offerType)
  const translatedOfferTypeString = t(offerTypeString, { ns: 'common' })

  const defaultDepositAmountPercentage = useAppSelector(getDefaultDepositAmountPercentage)
  // Enabled for a offer type as long as it is not a connection_tour v2 offer
  const shouldDisplayServiceFeeMessage: boolean = !INELEGIBLE_SERVICE_FEE_OFFER_TYPES.includes(offerType)

  const isOfferUsingDefaultDepositPercentage = isOfferDefaultDepositAmountPercentage(depositThresholds, depositAmount)

  const depositAmountKeyDetails: ReturnType<typeof getDepositAmountI18NKeyDetails> =
    isOfferUsingDefaultDepositPercentage ? { type: 'unspecified', options: {} } :
        !depositType ? { type: 'percentage', options: { amount: depositAmountPercentage } } :
          getDepositAmountI18NKeyDetails(currentCurrency, defaultDepositAmountPercentage, depositType, depositAmountPercentage)

  // Ensure deposit:eligibilityDescription has a matching variant for any value this will generate!
  const eligibilityDescriptionContext = `${isHotel ? 'showExclusions' : 'notShowExclusions'}${minAmount > 0 ? 'WithMinimumAmount' : 'WithoutMinimumAmount'}` as const

  return (
    <VerticalSpacer gap={16}>
      <BodyText variant="large" as="p">
        {t('payDepositIntro2', { ns: 'deposit', context: depositAmountKeyDetails.type, ...depositAmountKeyDetails.options, numberOfDays: paymentDueDaysBeforeCheckin })}
      </BodyText>
      <div>
        <Heading variant="heading6">{t('Eligibility', { ns: 'deposit' })}</Heading>
        <BodyText variant="large" as="p">
          {t('eligibilityDescription', { ns: 'deposit', context: eligibilityDescriptionContext, offerTypeString: translatedOfferTypeString, minAmount, numberOfDays: offerEligibilityDaysInAdvance, format: 'roundedDollar' })}
        </BodyText>
      </div>
      <div>
        <Heading variant="heading6">{t('Cancellation policy', { ns: 'deposit' })}</Heading>
        <BodyText variant="large" as="p">
          {isFlexibleCancellation && t('noRefundAfterCancellationPeriodFlexible', { ns: 'deposit' })}
          {!isFlexibleCancellation && t('noRefundAfterCancellationPeriodNonFlexible', { ns: 'deposit' })}
        </BodyText>
      </div>
      {isHotel && <BodyText variant="large" as="p">
        {t('refundOnlyAccommodation', { ns: 'deposit' })}
      </BodyText>}
      {shouldDisplayServiceFeeMessage && <div>
        <BodyText variant="large" as="p">
          {t('serviceFeeNotRefundable', { ns: 'deposit' })}
        </BodyText>
      </div>}
      {shouldDisplayServiceFeeMessage && <div>
        <Heading variant="heading6">{t('Service Fee', { ns: 'deposit' })}</Heading>
        <BodyText variant="large" as="p">
          {t('serviceFeePercentagePayable', { ns: 'deposit', feePercentage: SERVICE_FEE_PERCENTAGE })}
        </BodyText>
      </div>}
    </VerticalSpacer>
  )
}

export default DepositInfoModalContent
